import React, { useMemo } from "react";
import { Box, FormField, Heading, Select, Tip, Text } from "grommet";
import { CircleInformation } from "grommet-icons";
import { PMS_WEBREZPRO } from "@common/constants";
import { useTranslation } from "react-i18next";

export default function CheckinFlowApproval({ formValues, onChange, hotel }) {
  const { t } = useTranslation();

  const approval = formValues.checkinApproval || {
    autoApprove: "never",
    getNotified: "always",
  };

  const isWebRezPro = hotel?.pmsSlug === PMS_WEBREZPRO;
  const hasAiPreCheck =
    !!formValues.requestPhotoID && !!formValues.aiPreCheckEnabled;

  const baseNotifiedOptions = useMemo(
    () => [
      { label: t("checkinFlow.approval.notifyAlways"), value: "always" },
      { label: t("checkinFlow.approval.notifyAiNeg"), value: "aiNegative" },
    ],
    [t],
  );

  const autoApproveOptions = useMemo(() => {
    const options = [
      { label: t("checkinFlow.approval.approveAlways"), value: "always" },
    ];
    if (hasAiPreCheck) {
      options.push({
        label: t("checkinFlow.approval.approveAiPositive"),
        value: "aiPositive",
      });
    }
    if (isWebRezPro) {
      options.push({
        label: t("checkinFlow.approval.approveWrp"),
        value: "webrezproAgreement",
      });
      if (hasAiPreCheck) {
        options.push({
          label: t("checkinFlow.approval.approveWrpAi"),
          value: "webrezproAndAi",
        });
      }
    }
    options.push({
      label: t("checkinFlow.approval.approveNever"),
      value: "never",
    });
    return options;
  }, [hasAiPreCheck, isWebRezPro, t]);

  // If current selection is no longer available, fall back to "never"
  const currentAutoApprove = autoApproveOptions.some(
    (o) => o.value === approval.autoApprove,
  )
    ? approval.autoApprove
    : "never";

  const getNotifiedOptions = hasAiPreCheck
    ? baseNotifiedOptions
    : baseNotifiedOptions.filter((o) => o.value !== "aiNegative");

  const currentGetNotified = getNotifiedOptions.some(
    (o) => o.value === approval.getNotified,
  )
    ? approval.getNotified
    : "always";

  const handleChange = (field, value) => {
    onChange({
      ...formValues,
      checkinApproval: { ...approval, [field]: value },
    });
  };

  return (
    <Box id="section-approval" gap="medium">
      <Heading level={3} margin="none">
        {t("checkinFlow.approval.title")}
      </Heading>

      <Text size="small" color="dark-4">
        {t("checkinFlow.approval.description")}
      </Text>

      <Box gap="medium" width={{ max: "medium" }}>
        <FormField label={t("checkinFlow.approval.autoApprove")}>
          <Select
            size="medium"
            labelKey="label"
            valueKey={{ key: "value", reduce: true }}
            value={currentAutoApprove}
            options={autoApproveOptions}
            onChange={({ value }) => handleChange("autoApprove", value)}
          />
        </FormField>

        <Box direction="row" gap="small" align="center">
          <FormField label={t("checkinFlow.approval.getNotified")} flex>
            <Select
              size="medium"
              labelKey="label"
              valueKey={{ key: "value", reduce: true }}
              value={currentGetNotified}
              options={getNotifiedOptions}
              onChange={({ value }) => handleChange("getNotified", value)}
            />
          </FormField>
          <Tip content={t("checkinFlow.approval.notificationTip")}>
            <CircleInformation size="small" />
          </Tip>
        </Box>
      </Box>
    </Box>
  );
}
